/**
 * tRPC client for the VentureTrack backend.
 *
 * Authentication: sends session cookie automatically via credentials: 'include'.
 * Multi-tenancy: sends the active firm ID in X-Active-Firm-Id.
 *
 * Import `trpc` from this module to make direct (non-reactive) calls.
 * For reactive queries in Vue components, import from `./index.ts`.
 */
import { createTRPCClient, httpBatchLink, httpLink, isNonJsonSerializable, splitLink, TRPCClientError } from '@trpc/client';
import type { TRPCLink } from '@trpc/client';
import { observable } from '@trpc/server/observable';
import type { AppRouter } from '@backend/trpc';
import { getScope } from './scope';
import { getScopedQueryClient, invalidateDatedScopes } from './queryClients';

/** Backend origin, shared by the tRPC links and the document download route. */
export const BACKEND_ORIGIN = (import.meta.env.VITE_BACKEND_URL as string | undefined) ?? 'http://localhost:5001';
const API_BASE = `${BACKEND_ORIGIN}/trpc`;

/* Read per request, not captured: the header has to reflect the scope active at the
   moment the link runs. `@/trpc/scope` depends only on vue and localStorage, so this
   module stays free of Pinia and the router. */
export function linkHeaders() {
  const { firmId, asOf } = getScope();
  return {
    ...(firmId ? { 'X-Active-Firm-Id': firmId } : {}),
    ...(asOf ? { 'X-As-Of-Date': asOf } : {}),
  };
}

/* Firm header only, never the as-of date. Global search must stay live and unrewound
   (searchStore builds a firm-wide singleton index), but a deep link on an ?asOf URL
   activates the dated scope before the index loads — sending the date would bake an
   RLS-rewound index that persists after Return to today. */
function liveLinkHeaders() {
  const { firmId } = getScope();
  return firmId ? { 'X-Active-Firm-Id': firmId } : {};
}

/* Procedure prefixes that must always read live, whatever scope is active. */
const ALWAYS_LIVE_PATHS = ['searchIndex.'];

function linkFetch(url: RequestInfo | URL, options?: RequestInit) {
  return fetch(url, { ...options, credentials: 'include' });
}

/* Procedures slow enough to hurt anything batched alongside them. httpBatchLink
   resolves no call in a batch until every call in it has returned, so one slow
   procedure gates the whole request — on fund detail, searchIndex.getAll (a ~1.7MB
   firm-wide payload) held funds.getById for ~1.8s, which in turn delayed everything
   watching for the loaded fund.

   The criterion for this list is latency, not importance or page position: add a
   procedure here when it is reliably slow, so it pays its own cost instead of
   charging it to whatever happens to share its tick. */
const SLOW_PATHS = ['alerts.', 'searchIndex.', 'reports.run', 'reports.catalog'];

/*
 * The routers whose mutations run an alert scan (`scanForChanges` in `backend/src/trpc/routers/`).
 * A mutation on any other router changes no stored alert, so it does not refetch the alerts.
 * `alerts.*` is left out: its mutations refetch the alerts themselves (`useAlertDismiss`, the
 * alert settings page). `alertScanningRouters.test.ts` fails when
 * this list and the backend disagree.
 */
export const ALERT_SCANNING_ROUTERS = new Set([
  'bookValueMarkings',
  'capitalEvents',
  'companies',
  'companySecurities',
  'contacts',
  'escrows',
  'familyContacts',
  'forecasts',
  'fundNavs',
  'funds',
  'gpFees',
  'holdings',
  'investorContacts',
  'investorFamilies',
  'investors',
  'lotDocuments',
  'lots',
  'lpCommitments',
  'lpTransactions',
  'mailingLists',
  'mailingSubscriptions',
  'projections',
]);

/*
 * Client mirror of the backend's root as-of guard: while a date is
 * set, every mutation is rejected here instead of being sent — the 164 direct
 * `trpc.*.mutate()` call sites in the app get this for free, with no per-site check.
 *
 * On a successful mutation it also invalidates every OTHER scope's cache for the
 * active firm (`invalidateDatedScopes`) so a transaction back-dated to on-or-before a
 * date the user has already visited shows up on the next visit to that date, rather
 * than waiting out staleTime. A mutation can only fire from a live scope (the guard
 * above ensures that), so this never touches the scope the write came from.
 *
 * After a mutation on a router in ALERT_SCANNING_ROUTERS it also invalidates the live scope's
 * alerts. Those mutations rescan alerts before they return, so the stored alerts are already
 * current when the response arrives, and their call sites need no alert invalidation of their own.
 */
export const asOfMutationGuardLink: TRPCLink<AppRouter> = () => {
  return ({ op, next }) => {
    return observable((observer) => {
      if (op.type === 'mutation' && getScope().asOf !== null) {
        observer.error(new TRPCClientError('Editing is disabled while viewing a past date', {
          result: {
            error: {
              code: -32003,
              message: 'Editing is disabled while viewing a past date',
              data: { code: 'FORBIDDEN', httpStatus: 403 },
            },
          },
        }));
        return;
      }

      /*
       * CACHE-RESOLUTION RULE (queryClients.ts): capture the firm at dispatch, not at
       * settle. A firm switch while the mutation is in flight would otherwise invalidate
       * the NEW firm's dated caches and leave the mutated firm's dated scopes stale.
       */
      const dispatchScope = getScope();
      const dispatchFirmId = dispatchScope.firmId;

      const subscription = next(op).subscribe({
        next(value) {
          if (op.type === 'mutation') {
            invalidateDatedScopes(dispatchFirmId);

            // a path is `<router>.<procedure>`
            if (ALERT_SCANNING_ROUTERS.has(op.path.split('.')[0]))
              void getScopedQueryClient(dispatchScope).invalidateQueries({ queryKey: ['alerts'] });
          }

          observer.next(value);
        },
        error(err) {
          observer.error(err);
        },
        complete() {
          observer.complete();
        },
      });

      return () => subscription.unsubscribe();
    });
  };
};

/* Headers for an unbatched call: always-live paths drop the as-of date, everything else sends the scope. */
function unbatchedHeaders(path: string) {
  return ALWAYS_LIVE_PATHS.some((prefix) => path.startsWith(prefix)) ? liveLinkHeaders() : linkHeaders();
}

/** Typed tRPC client — use this for non-reactive calls (e.g., in stores, actions) */
export const trpc = createTRPCClient<AppRouter>({
  links: [
    asOfMutationGuardLink,
    splitLink({
      // Always-live paths ride the unbatched link too, so their header override below
      // cannot leak onto (or miss from) other procedures sharing a batch.
      condition(op) {
        // FormData uploads cannot be batched: httpBatchLink serializes inputs as JSON.
        return isNonJsonSerializable(op.input)
          || [...SLOW_PATHS, ...ALWAYS_LIVE_PATHS].some((path) => op.path.startsWith(path));
      },
      true: splitLink({
        // FormData uploads go as multipart, which only httpLink can send. An upload always has an input.
        condition: (op) => isNonJsonSerializable(op.input),
        true: httpLink({
          url: API_BASE,
          headers: ({ op }) => unbatchedHeaders(op.path),
          fetch: linkFetch,
        }),
        /* Every other unbatched call is a batch of one: its own request, so a slow procedure still
           holds up nothing else, in the batch wire format. tRPC's server reads a non-batch POST
           body as JSON, so a call with no input (an empty body) fails there, query or mutation;
           a batch body is an object keyed by call index, where no input is simply a missing key.
           Queries go as POST with the input in the body, so no input reaches the request logs
           (Fastify's or Cloud Run's) through the URL. The server allows it with allowMethodOverride. */
        false: httpBatchLink({
          url: API_BASE,
          methodOverride: 'POST',
          maxItems: 1,
          headers: ({ opList }) => unbatchedHeaders(opList[0].path),
          fetch: linkFetch,
        }),
      }),
      false: httpBatchLink({
        url: API_BASE,
        methodOverride: 'POST',
        headers: linkHeaders,
        fetch: linkFetch,
        /* A batch puts every procedure name in the URL (inputs ride in the body), so a page
           that fires enough queries in one tick can outgrow what the server will route.
           tRPC splits into additional requests rather than exceeding this, which
           degrades gracefully; without it the batch is silently 404'd and every query
           in it fails at once. Kept below the backend's maxParamLength (see
           backend/index.ts) and well under typical proxy URL limits. */
        maxURLLength: 4000,
        /* Bounds how much work one request can ask for. The backend's old 500-char
           maxParamLength capped this implicitly at ~17 procedures; raising that limit
           removed the cap, so state it explicitly here instead of letting it ride on
           how long the procedure names happen to be. Over the limit, tRPC splits into
           another parallel request — no wave is added. Fund detail, the heaviest page,
           batches 20. */
        maxItems: 30,
      }),
    }),
  ],
});
